import {useCallback, useMemo} from 'react'
import {useAnalytics} from '@github-ui/use-analytics'
import type {ManageBatchEntrySurface} from '../../../contexts/DockedPanelContext'
import {useActiveRoute} from '../../../hooks/use-active-route'
import {useLayoutStoreInstance} from '../../../stores/LayoutStore'
import {useUnifiedBatch} from '../../../stores/layout-selectors'
import type {UnifiedBatchItem} from '../../../stores/slices/unified-batch'
import type {ManageBatchTab} from '../ManageBatchPanel'

/**
 * Panel-level telemetry for the unified Manage batch surface.
 *
 * Emits via `useAnalytics().sendAnalyticsEvent(name, target, payload)`,
 * matching the precedent set in
 * https://github.com/github/github-ui/pull/20679 for the legacy per-comment
 * suggestion-batch actions (`comments.commit_suggested_change_from_batch`,
 * `comments.add_suggested_change_to_batch`, etc.). Every event carries the
 * shared `commentKind` (`'ccr' | 'human' | 'mixed'`) and `surface`
 * (`useActiveRoute()`) dimensions so the Unified PR review batch dashboard
 * (#790) can slice the same way the legacy dashboards do.
 *
 * Call sites:
 *  - `unified_batch_panel.open` is NOT emitted from here. It is emitted by
 *    `ManageBatchOpenTelemetry` when the panel actually becomes the active
 *    docked panel, reading its `entrySurface` from panel metadata. Openers
 *    therefore cannot forget to instrument themselves, and re-clicking an
 *    opener while the panel is already open cannot double-count.
 *  - `onPanelClose` is fired by the shell's Cancel/Discard footer buttons and
 *    carries a `closeReason` so Cancel (batch preserved) and Discard (batch
 *    cleared) can be told apart on the dashboard.
 *  - `onTabSelected` is fired by the shell's tab nav.
 *  - `onSubmit` / `onSubmitSuccess` / `onSubmitFailure` are fired by the
 *    tab-content sub-issues (Commit suggestions, Fix with Copilot).
 *
 * Terminal batch events — `unified_batch_panel.submit_success` and
 * `unified_batch_panel.close` with `closeReason: 'discard'` — additionally carry
 * the {@link BatchCompositionCounts}. Only these terminal events should
 * be used for final-composition queries; summing snapshots from `open`,
 * `tab_selected`, `submit`, etc. would count the same batch multiple times.
 */
export interface ManageBatchTelemetry {
  onPanelClose: (closeReason: ManageBatchCloseReason) => void
  onTabSelected: (tab: ManageBatchTab, selectionSource?: ManageBatchTabSelectionSource) => void
  onSubmit: (tab: ManageBatchTab) => void
  onSubmitSuccess: (tab: ManageBatchTab) => void
  onSubmitFailure: (tab: ManageBatchTab, error: unknown) => void
}

export type ManageBatchTabSelectionSource = 'tab_click' | 'disallowed_tab_nudge'

/**
 * Why the Manage batch panel closed:
 *  - `'cancel'` — footer Cancel; the batch is preserved for later, so this is
 *    not abandonment.
 *  - `'discard'` — footer Discard batch; the batch is cleared.
 *
 * Both still emit `unified_batch_panel.close`, so the existing event name stays
 * backward compatible; the `closeReason` field lets new queries separate the
 * two outcomes.
 */
export type ManageBatchCloseReason = 'cancel' | 'discard'

/**
 * Composition of a batch, one count per {@link UnifiedBatchItem} `kind`.
 *
 * Attached to terminal batch events so the Unified PR review batch dashboard
 * can reconstruct the requested categories without further
 * instrumentation:
 *  - Human comments → `humanWithoutSuggestionCount`
 *  - CCR comments → `ccrWithoutSuggestionCount`
 *  - Direct commit suggestions → `humanWithSuggestionCount + ccrWithSuggestionCount`
 *
 * All four primitives are retained (rather than the derived categories) so
 * future human-vs-CCR suggestion comparisons need no new events. The sum of the
 * four counts always equals `batchSize`.
 */
export type BatchCompositionCounts = {
  ccrWithSuggestionCount: number
  ccrWithoutSuggestionCount: number
  humanWithSuggestionCount: number
  humanWithoutSuggestionCount: number
}

const TARGET = 'UNIFIED_BATCH_PANEL'

type EventPayload = Record<string, unknown>

/**
 * Emits `unified_batch_panel.open` for a single panel-open transition.
 *
 * Split out of {@link useManageBatchTelemetry} because its only caller,
 * `ManageBatchOpenTelemetry`, is mounted by the docked-panel shell for the
 * lifetime of the page. Subscribing to the batch there (as
 * `useManageBatchTelemetry` does via `useUnifiedBatch`) would re-render the
 * shell on every add/remove, so the batch is read imperatively at emit time
 * instead. The payload is identical to the other panel events.
 */
export function useManageBatchOpenTelemetry(): (entrySurface: ManageBatchEntrySurface) => void {
  const {sendAnalyticsEvent} = useAnalytics()
  const surface = useActiveRoute()
  const layoutStore = useLayoutStoreInstance()

  return useCallback(
    (entrySurface: ManageBatchEntrySurface) => {
      const {items} = layoutStore.getState().unifiedBatch
      sendAnalyticsEvent('unified_batch_panel.open', TARGET, {
        surface,
        commentKind: deriveBatchCommentKind(items),
        batchSize: items.length,
        entrySurface,
      })
    },
    [layoutStore, sendAnalyticsEvent, surface],
  )
}

export function useManageBatchTelemetry(): ManageBatchTelemetry {
  const {sendAnalyticsEvent} = useAnalytics()
  const surface = useActiveRoute()
  const {items} = useUnifiedBatch()
  const commentKind = deriveBatchCommentKind(items)
  const batchSize = items.length
  const compositionCounts = useMemo(() => getBatchCompositionCounts(items), [items])

  const emit = useCallback(
    (eventName: string, extras: EventPayload = {}) => {
      sendAnalyticsEvent(eventName, TARGET, {surface, commentKind, batchSize, ...extras})
    },
    [sendAnalyticsEvent, surface, commentKind, batchSize],
  )

  // Discard is a terminal batch event, so it carries the composition.
  // `handleDiscard` fires this before `clearBatch()`, capturing the counts from
  // the render-time `items` snapshot so the pre-clear composition is retained.
  const onPanelClose = useCallback(
    (closeReason: ManageBatchCloseReason) =>
      emit('unified_batch_panel.close', {
        closeReason,
        ...(closeReason === 'discard' ? compositionCounts : {}),
      }),
    [emit, compositionCounts],
  )
  const onTabSelected = useCallback(
    (tab: ManageBatchTab, selectionSource: ManageBatchTabSelectionSource = 'tab_click') =>
      emit('unified_batch_panel.tab_selected', {tab, selectionSource}),
    [emit],
  )
  const onSubmit = useCallback((tab: ManageBatchTab) => emit('unified_batch_panel.submit', {tab}), [emit])
  // The Commit and Fix flows clear the batch in their mutation's `onSuccess`
  // before `mutateAsync` resolves, so this callback must emit the pre-submit
  // snapshot. It does: the tab providers capture this `onSubmitSuccess` (and the
  // `compositionCounts` it closes over) at click time, so the full-batch counts
  // survive the clear — the same closure-capture that keeps `batchSize` correct.
  const onSubmitSuccess = useCallback(
    (tab: ManageBatchTab) => emit('unified_batch_panel.submit_success', {tab, ...compositionCounts}),
    [emit, compositionCounts],
  )
  const onSubmitFailure = useCallback(
    (tab: ManageBatchTab, error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      emit('unified_batch_panel.submit_failure', {tab, errorMessage})
    },
    [emit],
  )

  return useMemo(
    () => ({
      onPanelClose,
      onTabSelected,
      onSubmit,
      onSubmitSuccess,
      onSubmitFailure,
    }),
    [onPanelClose, onTabSelected, onSubmit, onSubmitSuccess, onSubmitFailure],
  )
}

/**
 * Derives the composite `commentKind` for a batch, matching the precedent in
 * `PullRequestFilesToolbar` for the legacy suggestion batch:
 *  - `'human'` — every item is from a human review (or batch is empty)
 *  - `'ccr'` — every item is from automated review (CCR)
 *  - `'mixed'` — batch contains both human and CCR items
 *
 * The finer composition split is derived separately by
 * {@link getBatchCompositionCounts} and attached to terminal events. The coarse
 * `commentKind` is retained on every event because it matches what existing
 * per-comment events emit, which keeps cross-flow comparisons straightforward.
 */
function deriveBatchCommentKind(items: UnifiedBatchItem[]): 'ccr' | 'human' | 'mixed' {
  if (items.length === 0) return 'human'
  const hasCcr = items.some(item => item.kind.startsWith('ccr'))
  const hasHuman = items.some(item => item.kind.startsWith('human'))
  if (hasCcr && hasHuman) return 'mixed'
  if (hasCcr) return 'ccr'
  return 'human'
}

/**
 * Reduces a batch into per-`kind` counts for {@link BatchCompositionCounts}.
 *
 * Pure and side-effect free so it can be unit-tested directly and reused by any
 * terminal-event caller. Items are matched exhaustively on the
 * `UnifiedBatchItem['kind']` union, so the returned counts always sum to
 * `items.length`.
 */
export function getBatchCompositionCounts(items: UnifiedBatchItem[]): BatchCompositionCounts {
  const counts: BatchCompositionCounts = {
    ccrWithSuggestionCount: 0,
    ccrWithoutSuggestionCount: 0,
    humanWithSuggestionCount: 0,
    humanWithoutSuggestionCount: 0,
  }

  for (const item of items) {
    switch (item.kind) {
      case 'ccr-with-suggestion':
        counts.ccrWithSuggestionCount++
        break
      case 'ccr-without-suggestion':
        counts.ccrWithoutSuggestionCount++
        break
      case 'human-with-suggestion':
        counts.humanWithSuggestionCount++
        break
      case 'human-without-suggestion':
        counts.humanWithoutSuggestionCount++
        break
      default:
        // Compile-time exhaustiveness guard (no runtime effect): `item` narrows to
        // `never` once every `UnifiedBatchItem['kind']` is handled above. If the
        // union gains a member, `item` is that member here and this stops
        // type-checking until the switch and `BatchCompositionCounts` are updated,
        // so the counts can't silently sum below `batchSize`. Items can come from
        // persisted storage, so we deliberately don't throw on an unknown kind at
        // runtime.
        item satisfies never
    }
  }

  return counts
}
